import { useEffect, useRef, useState } from "react";
import { Star, Camera, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STAR_LABELS = ["", "Poor", "Fair", "Good", "Great", "Amazing"];

function fireConfetti() {
  const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 50 };
  const burst = (opts) => confetti({ ...defaults, ...opts });

  // Two side cannons
  burst({ particleCount: 70, origin: { x: 0.15, y: 0.55 }, angle: 60, spread: 70 });
  burst({ particleCount: 70, origin: { x: 0.85, y: 0.55 }, angle: 120, spread: 70 });
  // Top shower
  setTimeout(() => burst({ particleCount: 120, origin: { x: 0.5, y: 0 }, spread: 160 }), 250);
}

/**
 * Rating widget shown on the public /track/:code page once a package has
 * been DELIVERED. One-tap stars, optional comment, optional photo.
 */
export default function DeliveryRatingCard({ code, data, onRated }) {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file.");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image is too large (max 4MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (stars < 1) {
      toast.error("Tap a star to rate.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/public/track/${encodeURIComponent(code)}/rate`, {
        stars,
        comment,
        photo: photo || "",
      });
      toast.success("Thanks for rating! ⭐");
      fireConfetti();
      onRated?.(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // Pop a small celebratory burst the first time this card mounts.
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const t = setTimeout(fireConfetti, 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      data-testid="rating-card"
      className="relative overflow-hidden border border-cyan-500/40 rounded-xl p-6 sm:p-7 bg-gradient-to-br from-cyan-500/10 via-emerald-500/5 to-transparent"
    >
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-cyan-400/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-emerald-400/15 blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="flex items-center gap-2 text-cyan-600 dark:text-cyan-300 text-[10px] font-mono-tech uppercase tracking-[0.2em] mb-2">
          <Sparkles className="w-3.5 h-3.5" /> / Delivered
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter mb-1">
          Rate your delivery
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
          One tap helps {data.store?.name || "the store"} and the rider — and
          shows future customers what to expect.
        </p>

        <div className="flex items-center gap-1.5 mb-1" data-testid="rating-stars">
          {[1, 2, 3, 4, 5].map((n) => {
            const active = (hover || stars) >= n;
            return (
              <button
                key={n}
                type="button"
                data-testid={`rating-star-${n}`}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setStars(n)}
                className={`p-1 transition-transform hover:scale-110 ${
                  active ? "text-amber-400" : "text-slate-400 dark:text-slate-700"
                }`}
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
              >
                <Star className="w-8 h-8" fill={active ? "currentColor" : "none"} strokeWidth={1.6} />
              </button>
            );
          })}
        </div>
        <div className="h-5 text-xs font-mono-tech uppercase tracking-wider text-slate-500 mb-4">
          {(hover || stars) > 0 ? STAR_LABELS[hover || stars] : "Tap to rate"}
        </div>

        <Textarea
          data-testid="rating-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, 600))}
          placeholder="Optional — leave a quick note for the store or rider…"
          rows={3}
          className="mb-3 bg-white/70 dark:bg-slate-950/40"
        />

        {photo ? (
          <div className="relative mb-3 inline-block" data-testid="rating-photo-preview">
            <img src={photo} alt="Your rating photo" className="h-24 rounded-md border border-slate-200 dark:border-slate-800 object-cover" />
            <button
              type="button"
              onClick={() => setPhoto(null)}
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-900 text-white text-xs grid place-items-center hover:bg-red-500"
              data-testid="rating-photo-remove"
            >
              ×
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="rating-photo-pick"
            className="mb-3 inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-[0.18em] text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white border border-dashed border-slate-300 dark:border-slate-700 rounded-full px-3 py-1.5"
          >
            <Camera className="w-3.5 h-3.5" /> Add photo (optional)
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPickPhoto}
          data-testid="rating-photo-input"
        />

        <div className="flex items-center gap-3">
          <Button
            data-testid="rating-submit"
            disabled={submitting || stars < 1}
            onClick={submit}
            className="bg-cyan-500 hover:bg-cyan-400 dark:bg-cyan-400 dark:hover:bg-cyan-300 text-slate-950 font-medium h-11 px-6"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
            Submit rating
          </Button>
          <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
            <ShieldCheck className="w-3 h-3" /> One rating per package
          </span>
        </div>
      </div>
    </section>
  );
}

export { fireConfetti };
